/**
 * Preschool Quality & Ops → Week Plan (Shruti, 29 Oct): one short list a CD
 * can do in a week — 3 teachers (lowest overall) and the 2 centre jobs that
 * matter most — then Bonus: extra jobs to start once that list is done. Every item has
 * Log action, like the Actions tab.
 *
 * Centre jobs come from the server (fetchCdWeekPlan); the teachers are
 * picked here from the Teacher Scorecard (cdWeekTeacherRules), for roles
 * that may see it.
 *
 * Logs save to the server where it saves them (log_saves). A local setup on
 * the live database doesn't, so there they stay in this browser only.
 */
import { CircularProgress, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useMemo, useState } from 'react'

import {
  fetchCdWeekPlan,
  saveCdWeekLog,
  saveCdWeekPlan
} from 'api/centreOpsReport'
import { getTeacherScorecard } from 'api/teacherScorecard'
import { kekaCentreFor } from 'app/pages/Audits/TeacherScorecard/centreMatch'
import { C } from 'app/pages/Audits/TeacherScorecard/Punctuality'
import { addDays, band } from 'app/pages/Audits/TeacherScorecard/scoring'
import useCalendarDaysOff from 'app/pages/Audits/TeacherScorecard/useCalendarDaysOff'
import { centreOf } from 'app/pages/Teacher/Attendance/Attendance'

import { weekPlan } from './cdWeekTeacherRules'

// The 4 weeks the plan scores on (Doing well compares the last two).
const SCORECARD_WEEKS = 4
const NOTE_MAX = 500

const BAND_STYLE = {
  good: { background: C.goodBg, color: C.good },
  ok: { background: C.okBg, color: C.ok },
  weak: { background: C.weakBg, color: C.weak },
  none: { background: '#F3F4F6', color: '#9AA0A8' }
}

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
const mondayOf = (iso) => {
  const day = new Date(`${iso}T00:00:00`).getDay()
  return addDays(iso, -((day + 6) % 7))
}
// A week's plan is ready from the Saturday before at 12 noon (Shruti,
// 29 Oct): open on next week from then, otherwise this week (as the server
// does).
const READY_HOUR = 12
const planMonday = (now = new Date()) => {
  const monday = mondayOf(isoDay(now))
  const ready = new Date(`${addDays(monday, 5)}T00:00:00`)
  ready.setHours(READY_HOUR)
  return now >= ready ? addDays(monday, 7) : monday
}
const fmtWhen = (iso) =>
  new Date(iso).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  })
const fmt = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })

// Browser-only plan and logs, where the server doesn't save them (a local
// setup on the live database).
const logsKey = (centre, week) => `cdWeekLogs|${centre}|${week}`
const planKey = (centre, week) => `cdWeekPlan|${centre}|${week}`
const readJson = (key, empty) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || empty
  } catch (e) {
    return empty
  }
}
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // private window or storage off: it just isn't kept
  }
}
// Last week's items never logged (browser-only copy of the server's rule).
const localCarried = (centre, week) => {
  const prev = addDays(week, -7)
  const saved = readJson(planKey(centre, prev), null)
  if (!saved) return []
  const logged = new Set(
    readJson(logsKey(centre, prev), []).map((l) => l.item_key)
  )
  return saved.plan.main.map((x) => x.key).filter((k) => !logged.has(k))
}

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', gap: 16 },
  bar: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' },
  sub: { color: C.ink2, fontSize: 13 },
  card: {
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: '14px 18px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0
  },
  cardHead: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8
  },
  h2: { fontSize: 16, fontWeight: 800 },
  progress: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 },
  progressBar: {
    width: 120,
    height: 8,
    borderRadius: 4,
    background: '#EEF0F2',
    overflow: 'hidden'
  },
  row: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(150px, 1fr) minmax(200px, 1.6fr) minmax(220px, 1.8fr) 150px',
    gap: 16,
    alignItems: 'start',
    borderTop: `1px solid ${C.line}`,
    paddingTop: 12,
    fontSize: 13.5,
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' }
  },
  who: { fontSize: 14.5, fontWeight: 700 },
  // Normal case, never capitals (as the Actions tab).
  lbl: { fontSize: 12.5, fontWeight: 600, color: C.ink2, marginBottom: 2 },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 4, margin: '6px 0 2px' },
  pill: {
    fontSize: 12,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 999,
    whiteSpace: 'nowrap'
  },
  steps: { margin: 0, paddingLeft: 18, '& li': { marginBottom: 4 } },
  btn: {
    minHeight: 34,
    padding: '0 14px',
    borderRadius: 8,
    border: '1px solid #E2572B',
    background: '#E2572B',
    color: '#fff',
    font: 'inherit',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.6, cursor: 'default' }
  },
  ghost: { background: '#fff', color: '#E2572B' },
  doneTag: { color: C.good, fontWeight: 700 },
  lock: { fontSize: 13, fontWeight: 600, color: C.ink2 },
  since: { fontSize: 12, color: C.weak, fontWeight: 600, marginTop: 2 },
  log: {
    gridColumn: '1 / -1',
    background: C.band,
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  logRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: {
    flex: '1 1 260px',
    minHeight: 34,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${C.line}`,
    font: 'inherit',
    fontSize: 13.5
  },
  err: { color: C.weak, fontSize: 13 },
  empty: { display: 'flex', gap: 10, alignItems: 'center', color: C.ink2 }
}))

const ItemRow = ({
  item,
  log,
  logging,
  setLogging,
  onSave,
  busy,
  locked,
  carried
}) => {
  const classes = useStyles()
  const open = logging && logging.key === item.key
  return (
    <div className={classes.row}>
      <div>
        <div className={classes.who}>{item.title}</div>
        {carried && (
          <div className={classes.since}>Carried over from last week</div>
        )}
        {item.pills.length > 0 && (
          <div className={classes.pills}>
            {item.pills.map((p) => (
              <span
                key={p.text}
                className={classes.pill}
                style={BAND_STYLE[band(p.v)]}
              >
                {p.text}
              </span>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className={classes.lbl}>
          {item.kind === 'praise'
            ? 'What she did well'
            : 'Why it’s on your list'}
        </div>
        {item.fact}
      </div>
      <div>
        <div className={classes.lbl}>This week</div>
        {item.steps.length > 1 ? (
          <ul className={classes.steps}>
            {item.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        ) : (
          item.steps[0]
        )}
      </div>
      <div>
        {log && !open ? (
          <>
            <span className={classes.doneTag}>✓ Done</span>
            <div className={classes.sub}>{log.note}</div>
            {log.by && (
              <div className={classes.sub}>
                {log.by} · {fmt(log.at.slice(0, 10))}
              </div>
            )}
            {!locked && (
              <button
                type="button"
                className={`${classes.btn} ${classes.ghost}`}
                style={{ marginTop: 6, minHeight: 28 }}
                onClick={() => setLogging({ key: item.key, note: log.note })}
              >
                Change
              </button>
            )}
          </>
        ) : (
          !open &&
          (locked ? (
            <span className={classes.sub}>Not logged</span>
          ) : (
            <button
              type="button"
              className={classes.btn}
              onClick={() => setLogging({ key: item.key, note: '' })}
            >
              Log action
            </button>
          ))
        )}
      </div>
      {open && (
        <div className={classes.log}>
          <b>
            {item.kind === 'praise'
              ? `How did you appreciate ${item.title.split(' ')[0]}?`
              : `What did you do${
                  item.kind === 'teacher'
                    ? ` with ${item.title.split(' ')[0]}`
                    : ''
                }?`}
          </b>
          <div className={classes.logRow}>
            <input
              className={classes.input}
              value={logging.note}
              maxLength={NOTE_MAX}
              placeholder="What you did, in one line"
              autoFocus
              onChange={(e) => {
                const note = e.target.value
                setLogging((l) => ({ ...l, note }))
              }}
              onKeyDown={(e) => e.key === 'Enter' && onSave(item)}
            />
            <button
              type="button"
              className={classes.btn}
              disabled={busy || !logging.note.trim()}
              onClick={() => onSave(item)}
            >
              Save
            </button>
            <button
              type="button"
              className={`${classes.btn} ${classes.ghost}`}
              onClick={() => setLogging(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const CdWeekPlan = ({ centre, centreName, canScorecard }) => {
  const classes = useStyles()
  const [week, setWeek] = useState(() => planMonday())
  const [server, setServer] = useState(null)
  const [error, setError] = useState(null)
  const [scorecard, setScorecard] = useState(null)
  const [scoreError, setScoreError] = useState(false)
  // The plan shown: as saved (server or browser), or worked out now.
  const [shown, setShown] = useState(null)
  const [logs, setLogs] = useState([])
  const [logging, setLogging] = useState(null)
  const [busy, setBusy] = useState(false)
  const [logError, setLogError] = useState(null)
  const calendar = useCalendarDaysOff()

  useEffect(() => {
    if (!centre) return undefined
    let cancelled = false
    setServer(null)
    setShown(null)
    setError(null)
    setLogging(null)
    fetchCdWeekPlan({ centre, week })
      .then((d) => {
        if (cancelled) return
        setServer(d)
        setLogs(
          d.saves ? d.logs || [] : readJson(logsKey(centre, d.week_start), [])
        )
      })
      .catch(() => !cancelled && setError('Could not load the week plan.'))
    return () => {
      cancelled = true
    }
  }, [centre, week])

  // Only a plan still to be made needs the scorecard: the 4 weeks up to the
  // Friday before the week.
  const stored =
    server && !server.saves
      ? readJson(planKey(centre, server.week_start), null)
      : null
  const toMake = !!(server && server.ready && !server.saved && !stored)
  const scoreEnd = addDays(week, -3)
  useEffect(() => {
    if (!canScorecard || !toMake) return undefined
    let cancelled = false
    setScorecard(null)
    setScoreError(false)
    getTeacherScorecard({ end: scoreEnd, weeks: SCORECARD_WEEKS })
      .then((d) => !cancelled && setScorecard(d))
      .catch(() => !cancelled && setScoreError(true))
    return () => {
      cancelled = true
    }
  }, [canScorecard, toMake, scoreEnd])

  const teachersReady =
    !canScorecard || scoreError || (scorecard && calendar !== 'loading')

  const built = useMemo(() => {
    if (!toMake || !teachersReady) return null
    let teachers = []
    if (scorecard && !scoreError) {
      const kekaCentre = kekaCentreFor(centreName, [
        ...new Set(scorecard.teachers.map(centreOf))
      ])
      teachers = kekaCentre
        ? scorecard.teachers.filter((t) => centreOf(t) === kekaCentre)
        : []
    }
    return weekPlan({
      teachers,
      weeks: (scorecard && scorecard.weeks) || [],
      monday: server.week_start,
      centreActions: server.actions
    })
  }, [toMake, teachersReady, scorecard, scoreError, centreName, server])

  // Saved plan → as saved. Otherwise make it once and keep it: on the
  // server (the first save wins), or in this browser where the server
  // doesn't save. A past week never saved is shown worked out, not kept.
  useEffect(() => {
    if (!server || !server.ready) return undefined
    if (server.saved) {
      setShown({ plan: server.saved.plan, madeAt: server.saved.made_at })
      return undefined
    }
    if (stored) {
      setShown({ plan: stored.plan, madeAt: stored.made_at })
      return undefined
    }
    if (!built) return undefined
    if (server.locked) {
      setShown({ plan: built, madeAt: null })
      return undefined
    }
    if (!server.saves) {
      const keep = { plan: built, made_at: new Date().toISOString() }
      writeJson(planKey(centre, server.week_start), keep)
      setShown({ plan: built, madeAt: keep.made_at })
      return undefined
    }
    let cancelled = false
    saveCdWeekPlan({ centre, week: server.week_start, plan: built })
      .then((d) => !cancelled && setShown({ plan: d.plan, madeAt: d.made_at }))
      .catch(() => !cancelled && setShown({ plan: built, madeAt: null }))
    return () => {
      cancelled = true
    }
  }, [server, built])

  const plan = shown && shown.plan
  const carried = new Set(
    server
      ? server.saves
        ? server.carried || []
        : localCarried(centre, server.week_start)
      : []
  )
  const logOf = (key) => logs.find((l) => l.item_key === key)
  // Once the week is over it is view only (Shruti, 29 Oct).
  const locked = !!(server && server.locked)

  const save = async (item) => {
    const note = logging.note.trim()
    if (!note) return
    setBusy(true)
    setLogError(null)
    try {
      const saved = server.saves
        ? await saveCdWeekLog({
            centre,
            week: server.week_start,
            itemKey: item.key,
            title: item.title,
            note
          })
        : {
            item_key: item.key,
            title: item.title,
            note,
            by: '',
            at: new Date().toISOString()
          }
      const next = [...logs.filter((l) => l.item_key !== item.key), saved]
      if (!server.saves) writeJson(logsKey(centre, server.week_start), next)
      setLogs(next)
      setLogging(null)
    } catch (e) {
      setLogError('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!centre) return <div className={classes.sub}>Pick a centre.</div>

  const rows = (items) =>
    items.map((item) => (
      <ItemRow
        key={item.key}
        item={item}
        log={logOf(item.key)}
        logging={logging}
        setLogging={setLogging}
        onSave={save}
        busy={busy}
        locked={locked}
        carried={carried.has(item.key) && !logOf(item.key)}
      />
    ))
  const loggedMain = plan
    ? plan.main.filter((item) => logOf(item.key)).length
    : 0

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        <button
          type="button"
          className={`${classes.btn} ${classes.ghost}`}
          aria-label="Week before"
          onClick={() => setWeek(addDays(week, -7))}
        >
          ◀
        </button>
        <TextField
          type="date"
          size="small"
          variant="outlined"
          label="Week of"
          style={{ background: '#fff' }}
          value={week}
          onChange={(e) => e.target.value && setWeek(mondayOf(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
        <button
          type="button"
          className={`${classes.btn} ${classes.ghost}`}
          aria-label="Week after"
          onClick={() => setWeek(addDays(week, 7))}
        >
          ▶
        </button>
        {locked && (
          <span className={classes.lock}>🔒 Week over · view only</span>
        )}
        {server && server.ready && (
          <span className={classes.sub}>
            {fmt(server.week_start)} – {fmt(server.week_end)} · made from{' '}
            {fmt(server.basis_from)} – {fmt(server.basis_to)}
            {shown && shown.madeAt && ` · saved ${fmtWhen(shown.madeAt)}`}
          </span>
        )}
      </div>

      {error && <div className={classes.err}>{error}</div>}
      {server && !server.ready && (
        <div className={classes.card}>
          <span className={classes.h2}>Not ready yet</span>
          <span className={classes.sub}>
            The plan for the week of {fmt(server.week_start)} will be ready on{' '}
            {fmtWhen(server.ready_at)}. It is made from the 4 weeks up to{' '}
            {fmt(server.basis_to)}.
          </span>
        </div>
      )}
      {server && server.ready && locked && shown && !shown.madeAt && (
        <div className={classes.sub}>
          This week’s plan was not saved at the time, so this is worked out
          again from the numbers.
        </div>
      )}
      {(!server || server.ready) && !plan && !error && (
        <div className={classes.empty}>
          <CircularProgress size={20} /> Working out the week…
        </div>
      )}
      {scoreError && (
        <div className={classes.sub}>
          Could not load the Teacher Scorecard, so there are no teachers here.
        </div>
      )}
      {server && !server.saves && !locked && server.ready && (
        <div className={classes.sub}>
          The plan and logs are kept in this browser only here (not saved to
          Alfred).
        </div>
      )}
      {logError && <div className={classes.err}>{logError}</div>}

      {plan && plan.main.length > 0 && (
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <span className={classes.h2}>
              {locked ? 'That week’s list' : 'Do these this week'}
            </span>
            <span className={classes.progress}>
              <span className={classes.progressBar}>
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    background: C.good,
                    width: `${(100 * loggedMain) / plan.main.length}%`
                  }}
                />
              </span>
              {loggedMain} of {plan.main.length} logged
            </span>
          </div>
          {rows(plan.main)}
        </div>
      )}

      {plan && plan.bonus.length > 0 && (
        <div className={classes.card}>
          <span className={classes.h2}>Bonus</span>
          {/* What Bonus means, for CDs (Shruti, 29 Oct). */}
          <span className={classes.sub}>
            Bonus jobs are extra work for this week. Start them only after
            everything in the list above is done. They still matter, just less
            than the list above.
          </span>
          {rows(plan.bonus)}
        </div>
      )}

      {plan && plan.praise.length > 0 && (
        <div className={classes.card}>
          <span className={classes.h2}>Doing well: appreciate them</span>
          {/* Consistency and improvement both count (Shruti, 29 Oct). */}
          <span className={classes.sub}>
            These teachers have been consistent or have improved over the last 4
            weeks. Tell them you have noticed.
          </span>
          {rows(plan.praise)}
        </div>
      )}

      {plan && !plan.main.length && (
        <div className={classes.sub}>Nothing needs doing this week.</div>
      )}
    </div>
  )
}

export default CdWeekPlan
